import CustomizationInfoDTO from "@/dto/order/CustomizationInfoDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import ShopType from "@/enum/ShopType";
import delay from "@/utils/delay";
import mainServerClient from "../http/mainServerClient";
import platformServer from "../http/platformServer";
import { CustomizationType } from "../lhApi/enum";

interface CustomizationAccount {
    companyId: number;

    balance: number;

    /**
     * 是否试用过
     */
    usedGift: boolean;
}

/**
 * 获取当前登录用户的来火定制账号信息
 */
export function getCustomizationAccount() {
    return mainServerClient.get<CustomizationAccount>("/customization_account");
}

/**
 * 免费试用
 */
export function postCustomizationAccountGift() {
    return mainServerClient.post("/customization_account/used_gift");
}

export enum CustomizationShopApprovalStatus {
    ACCEPTED = "ACCEPTED",
    PENDING_APPROVAL = "PENDING_APPROVAL",
    REFUSED = "REFUSED",
}

export interface CustomizationShopInfo {
    id: number;
    approvalStatus: CustomizationShopApprovalStatus;
    dateCreated: string;
    lastUpdated: string;
    sellerUsername: string;
    shopType: ShopType;
}

export interface CustomizationShop extends ShopDTO {
    shopInfo?: CustomizationShopInfo;
}

/**
 * 获取店铺概况列表
 */
export function getCustomizationShopList() {
    return mainServerClient.get<CustomizationShop[]>("/customization/shop/list");
}

interface CustomerCustomizationInfo {
    sellerUsername: string;
    shipEasySellerUsername: string;
    shipEasyShopType: ShopType;
    shopType: ShopType;
}

export function getCustomizationShopInfo(shopId: number) {
    return mainServerClient.get<CustomerCustomizationInfo>("/customization/shop/customizationInfo", {
        shopId,
    });
}

interface ApplyFormCustomizationParams {
    customizationSellerUsername: string;
    customizationShopType: ShopType;
    shopId: number;
}

/**
 * 向来火定制店铺申请授权
 */
export function applyFormCustomization(params: ApplyFormCustomizationParams) {
    return mainServerClient.post("/customization/shop/apply_for_customization", params);
}

/**
 * 删除店铺绑定信息
 */
export function deleteShopBindingCustomization(shopId: number) {
    return mainServerClient.post("/customization/shop/delete", null, {
        params: {
            shopId,
        },
    });
}

interface GetCustomizationShopAuthorizedInfoParmas {
    // customizationUsername: string;
    // customizationShopType: ShopType;
    shopId: number;
}

/**
 *  获取来火定制授权申请信息
 */
export function getCustomizationShopAuthorizedInfo(params: GetCustomizationShopAuthorizedInfoParmas) {
    return mainServerClient.get<CustomizationShopInfo>(
        "/customization/shop/customization_shop_authorized_info",
        params,
    );
}

/**
 * 生成来火定制token
 */
export function generateCustomizationToken(shopId: number) {
    return mainServerClient.post<string>("/customization/shop/authorized_code/generate", {
        shopId,
    });
}

export interface SkuCustomizationBinding {
    skuId: number;
    customizationType?: CustomizationType;
    bottomPanelId?: number;
    brandId?: number;
    caseMaterialIds?: number[];
    diyRuleGroupIds?: number[];
    diyRuleId?: number;
    modelId?: number;
    switchTypes?: SwitchTypes[];
    functionTypes?: FunctionType[];
    supplierShopId?: number;
}

export enum SwitchTypes {
    MY_DIY_RULE = "MY_DIY_RULE",
    ORIGINAL_RULE = "ORIGINAL_RULE",
    SYSTEM_RULE = "SYSTEM_RULE",
}

export enum FunctionType {
    UPLOAD_IMAGE = "UPLOAD_IMAGE",
    TAGS = "TAGS",
    MATERIALS = "MATERIALS",
    FONT = "FONT",
}

export const FunctionTypeNameMapping = {
    [FunctionType.FONT]: "文字",
    [FunctionType.MATERIALS]: "素材",
    [FunctionType.TAGS]: "贴纸",
    [FunctionType.UPLOAD_IMAGE]: "上传图片",
};

export function getSkuCustomizationBinding(shopId: number, skuIds: number[]) {
    return platformServer.post<SkuCustomizationBinding[]>(shopId, "/sku_customization_binding/batGet", skuIds, {
        params: {
            shopId,
        },
    });
}

export function saveSkuCustomizationBinding(shopId: number, skuBinding: SkuCustomizationBinding) {
    return platformServer.post(shopId, "/sku_customization_binding/save", skuBinding, {
        params: {
            shopId,
        },
    });
}

export interface QueryTransactionParams {
    shopId?: number;
    pageNo: number;
    pageSize: number;
    start?: string;
    end?: string;
}

export interface CustomizationTransaction {
    id: number;
    amount: number;
    orderId: number;
    orderNo: string;
    orderTime: string;
    shopId: number;
}

/**
 * 定制订单交易明细查询
 */
export function queryTransaction(data: QueryTransactionParams) {
    return mainServerClient.post<{ items: CustomizationTransaction[]; totalCount: number }>(
        "/customization/transaction/query",
        data,
    );
}

interface CustomizationServiceChargeItem {
    id: number;
    baseCost: number;
    customizationInfo: CustomizationInfoDTO;
    orderItemId: number;
    quantity: number;
}

enum CustomizationServiceChargeStatus {
    PAID = "PAID",
    PENDING_PAYMENT = "PENDING_PAYMENT",
}

export interface CustomizationServiceCharge {
    id: number;
    amount: number;
    chargeItems: CustomizationServiceChargeItem[];
    orderId: number;
    orderTime: string;
    paymentTime: string;
    shopId: number;
    status: CustomizationServiceChargeStatus;
}

/**
 * 获取定制订单详情
 */
export function getTransactionServiceCharge(shopId: number, orderId: number) {
    return platformServer.get<CustomizationServiceCharge>(shopId, "/customization_service_charge", {
        shopId,
        orderId,
    });
}

export interface CustomizationAddedValueProduct {
    id: number;
    enable: boolean;
    price: number;
    productId: number;
    productName: string;
    serviceId: number;
    serviceName: string;
    value: number;
    valueText: string;
}

/**
 * 获取来火定制增值付费产品列表
 */
export function getCustomizationAddedValueProduct() {
    return mainServerClient.get<CustomizationAddedValueProduct[]>("/customization_added_value_product");
}

interface GetCustomizationAddedValueOrderListParams {
    pageNo: number;

    pageSize: number;

    start?: string;

    end?: string;
}

export enum CustomizationAddedValueOrderStatus {
    CANCELED = "CANCELED",
    PAID = "PAID",
    REFUND = "REFUND",
    UNPAID = "UNPAID",
}
export interface CustomizationAddedValueOrder {
    id: number;
    companyId: number;
    created: string;
    operatingUsername: string;
    price: number;
    status: CustomizationAddedValueOrderStatus;
    tradeId: string;
    value: number;
    title: string;
}

/**
 * 获取订单列表
 */
export function getCustomizationAddedValueOrderList(params: GetCustomizationAddedValueOrderListParams) {
    return mainServerClient.post<{ items: CustomizationAddedValueOrder[]; totalCount: number }>(
        "/customization_added_value_order/list",
        params,
    );
}

interface PayCustomizationAddedValueOrderParams {
    title: string;
    productId: number;
    serviceId: number;
    quantity: string;
}

interface ReloadPaymentHtmlDto {
    /**
     * 增值订单ID
     */
    addedValueOrderId?: number;
    /**
     * 支付页面HTML
     * 用于给商家扫描支付
     */
    paymentHtml?: string;
}

/**
 * 支付来火定制增值订单
 */
export function payCustomizationAddedValueOrder(params: PayCustomizationAddedValueOrderParams) {
    return mainServerClient.post<ReloadPaymentHtmlDto>("/customization_account/prepaid/reload/pay", params);
}

/**
 * 同步未支付的订单
 */
export function syncCustomizationAddedValueOrder(addedValueOrderId: number) {
    return mainServerClient.post<CustomizationAddedValueOrder>(
        "/customization_account/prepaid/reload/sync_payment_status",
        {
            addedValueOrderId,
        },
    );
}

export async function syncUnpaidCustomizationAddedValueOrders() {
    const results = await getCustomizationAddedValueOrderList({
        pageNo: 1,
        pageSize: 10,
    });

    for (let i = 0; i < results?.items?.length; i++) {
        const order = results.items[i];

        if (order.status === CustomizationAddedValueOrderStatus.UNPAID) {
            await syncCustomizationAddedValueOrder(order.id);
        }
    }
}

/**
 * 批量支付未支付的平台订单 按下单先后顺序执行
 */
export async function payShopCustomizationOrder(shopId: number) {
    let payResult = await payCustomizationServiceCharge(shopId);

    if (payResult?.status && (payResult?.status === "COMPLETED" || payResult?.status === "ERROR")) {
        return payResult;
    }

    const taskId = payResult.taskId;

    while (payResult?.status === "RUNNING") {
        await delay(2000);

        payResult = await getPayCustomizationServiceChargeResult(shopId, taskId);

        if (payResult?.status !== "RUNNING") {
            break;
        }
    }

    return payResult;
}

/**
 * 批量支付未支付的平台订单 按下单先后顺序执行 (供货商使用)
 */
export async function payShopCustomizationOrderBySupplier(supplierShopId: number) {
    let payResult = await payCustomizationServiceChargeBySupplier(supplierShopId);

    if (payResult?.status && (payResult?.status === "COMPLETED" || payResult?.status === "ERROR")) {
        return payResult;
    }

    const taskId = payResult.taskId;
    while (payResult?.status === "RUNNING") {
        await delay(2000);

        payResult = await getPayCustomizationServiceChargeResult(supplierShopId, taskId);

        if (payResult?.status !== "RUNNING") {
            break;
        }
    }

    return payResult;
}

interface CustomizationServiceChargeProcess {
    taskId: string;
    results: {
        errorMsg: string;
        orderNo: string;
        successful: boolean;
    }[];

    status: "COMPLETED" | "ERROR" | "RUNNING";
}

function payCustomizationServiceCharge(shopId: number) {
    return platformServer.post<CustomizationServiceChargeProcess>(shopId, "/customization_service_charge/pay", null, {
        params: {
            shopId,
        },
    });
}

export function payCustomizationServiceChargeBySupplier(supplierShopId: number) {
    return platformServer.post<CustomizationServiceChargeProcess>(
        supplierShopId,
        "/customization_service_charge/payBySupplier",
        null,
        {
            params: {
                supplierShopId,
            },
        },
    );
}

function getPayCustomizationServiceChargeResult(shopId: number, taskId: string) {
    return platformServer.get<CustomizationServiceChargeProcess>(shopId, "/customization_service_charge/pay/result", {
        taskId,
    });
}
